import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { MTLLoader } from "three/examples/jsm/loaders/MTLLoader.js";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { Box } from "@mui/material";

const Visor3DPrenda = ({ modelUrl, height = 400, background = "#f5f5f5" }) => {
  const containerRef = useRef(null);
  const threeRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !modelUrl) return;

    const width = containerRef.current.clientWidth || 600;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(background);

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.set(0, 1.5, 3);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    containerRef.current.innerHTML = "";
    containerRef.current.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;

    const hemiLight = new THREE.HemisphereLight(0xffffff, 0x444444, 1.0);
    hemiLight.position.set(0, 20, 0);
    scene.add(hemiLight);

    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(3, 10, 10);
    scene.add(dirLight);

    const grid = new THREE.GridHelper(4, 8, 0x888888, 0xcccccc);
    grid.position.y = -1;
    scene.add(grid);

    const basePath = modelUrl.replace(/[^/]+$/, "");
    const mtlLoader = new MTLLoader();
    mtlLoader.setResourcePath(basePath);
    mtlLoader.setPath(basePath);

    const fileName = modelUrl.split("/").pop();
    const baseName = fileName ? fileName.replace(/\.obj$/i, "") : "";

    mtlLoader.load(
      `${baseName}.mtl`,
      (materials) => {
        materials.preload();
        const objLoader = new OBJLoader();
        objLoader.setMaterials(materials);
        objLoader.setPath(basePath);
        objLoader.load(
          fileName,
          (object) => {
            const box = new THREE.Box3().setFromObject(object);
            const size = new THREE.Vector3();
            box.getSize(size);
            const center = new THREE.Vector3();
            box.getCenter(center);
            object.position.sub(center);

            const maxDim = Math.max(size.x, size.y, size.z);
            if (maxDim > 0) {
              const scale = 2 / maxDim;
              object.scale.setScalar(scale);
            }

            scene.add(object);
          },
          undefined,
          (err) => {
            console.error("Error cargando OBJ:", err);
          },
        );
      },
      undefined,
      () => {
        const objLoader = new OBJLoader();
        objLoader.setPath(basePath);
        objLoader.load(
          fileName,
          (object) => {
            const box = new THREE.Box3().setFromObject(object);
            const size = new THREE.Vector3();
            box.getSize(size);
            const center = new THREE.Vector3();
            box.getCenter(center);
            object.position.sub(center);
            const maxDim = Math.max(size.x, size.y, size.z);
            if (maxDim > 0) {
              const scale = 2 / maxDim;
              object.scale.setScalar(scale);
            }
            scene.add(object);
          },
        );
      },
    );

    let frameId;
    const animate = () => {
      controls.update();
      renderer.render(scene, camera);
      frameId = window.requestAnimationFrame(animate);
    };
    animate();

    const handleResize = () => {
      if (!containerRef.current) return;
      const newWidth = containerRef.current.clientWidth || width;
      camera.aspect = newWidth / height;
      camera.updateProjectionMatrix();
      renderer.setSize(newWidth, height);
    };
    window.addEventListener("resize", handleResize);

    threeRef.current = { renderer, scene, camera, controls };

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", handleResize);
      if (threeRef.current) {
        threeRef.current.controls.dispose();
        threeRef.current.renderer.dispose();
      }
    };
  }, [modelUrl, height, background]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        height,
        backgroundColor: background,
        borderRadius: 2,
        overflow: "hidden",
      }}
    />
  );
};

export default Visor3DPrenda;

